import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { getSupplierAddConfig, getSupplierLabels } from '@/api/suppliers';
import { useKeycloakAuth } from '@/contexts/keycloak-auth-context';

export interface SupplierDisplayContextType {
  /** Normalized display name: 'supplier' | 'beneficiary' | custom (e.g. 'Vendor'). Default 'supplier' when no setting. */
  displayName: string;
  /** Singular and plural labels (e.g. Supplier/Suppliers or Beneficiary/Beneficiaries). */
  labels: { singular: string; plural: string };
  /** Whether the config has been loaded (so we can show supplier language until then). */
  isLoaded: boolean;
}

const SupplierDisplayContext = createContext<SupplierDisplayContextType | undefined>(undefined);

const DEFAULT_LABELS = getSupplierLabels('supplier');

export const useSupplierDisplay = (): SupplierDisplayContextType => {
  const context = useContext(SupplierDisplayContext);
  if (context === undefined) {
    throw new Error('useSupplierDisplay must be used within a SupplierDisplayProvider');
  }
  return context;
};

interface SupplierDisplayProviderProps {
  children: React.ReactNode;
}

export const SupplierDisplayProvider: React.FC<SupplierDisplayProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useKeycloakAuth();
  const partyId = user?.partyId ?? null;
  const [displayName, setDisplayName] = useState<string>('supplier');
  const [isLoaded, setIsLoaded] = useState(false);

  const fetchConfig = useCallback(async () => {
    if (!isAuthenticated || !partyId) {
      setDisplayName('supplier');
      setIsLoaded(true);
      return;
    }
    try {
      const config = await getSupplierAddConfig(partyId);
      setDisplayName(config.displayName ?? 'supplier');
    } catch {
      setDisplayName('supplier');
    } finally {
      setIsLoaded(true);
    }
  }, [isAuthenticated, partyId]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const labels = getSupplierLabels(displayName);

  const value: SupplierDisplayContextType = {
    displayName,
    labels: isLoaded ? labels : DEFAULT_LABELS,
    isLoaded,
  };

  return (
    <SupplierDisplayContext.Provider value={value}>{children}</SupplierDisplayContext.Provider>
  );
};
